// Configuration: maps internal keys to format config with display text, value, and autoparse
export const OUTPUT_FORMATS = {
  'Do not parse results': {text: 'Do not parse results', value: undefined, autoparse: undefined },
  'JSON': {text: 'Parse to JSON (certain domains only)', value: 'json', autoparse: true },
  'CSV': {text: 'Parse to CSV (certain domains only)', value: 'csv', autoparse: true },
  'Markdown': {text: 'Parse to Markdown (LLM-ready, all domains)', value: 'markdown', autoparse: undefined },
  'Text': {text: 'Parse to Text (LLM-ready, all domains)', value: 'text', autoparse: undefined },
} as const;

export type FormatKey = keyof typeof OUTPUT_FORMATS;

/**
 * Get display text from format value and autoparse setting
 * Based on Parameters Lookup Table:
 * - Do Not Parse Results: no params (autoparse undefined, format undefined/empty)
 * - Parse to JSON: autoparse=true ONLY (no output_format)
 * - Parse to CSV: autoparse=true; output_format=csv
 * - Parse to Markdown: autoparse=false; output_format=markdown
 * - Parse to Text: autoparse=false; output_format=text
 *
 * @param format - The backend format value (e.g., 'json', 'csv', 'markdown', 'text')
 * @param autoparse - The autoparse setting (true, false, or undefined)
 * @returns The display text to show to the user
 */
export const getOutputFormatDisplayText = (format: string | undefined, autoparse?: boolean | undefined): string => {
  const normalizedFormat = format ? format.toLowerCase().trim() : '';

  // Do Not Parse Results: no autoparse, no format (or empty format)
  if (autoparse === undefined && (!normalizedFormat || normalizedFormat === 'json')) {
    return OUTPUT_FORMATS['Do not parse results'].text;
  }

  // Parse to JSON: autoparse=true with no format or 'json' format
  if (autoparse === true && (!normalizedFormat || normalizedFormat === 'json')) {
    return OUTPUT_FORMATS['JSON'].text;
  }

  // Parse to CSV: autoparse=true; output_format=csv
  if (autoparse === true && normalizedFormat === 'csv') {
    return OUTPUT_FORMATS['CSV'].text;
  }

  // Parse to Markdown: output_format=markdown (autoparse can be false or undefined for LLM formats)
  if (normalizedFormat === 'markdown') {
    return OUTPUT_FORMATS['Markdown'].text;
  }

  // Parse to Text: output_format=text (autoparse can be false or undefined for LLM formats)
  if (normalizedFormat === 'text') {
    return OUTPUT_FORMATS['Text'].text;
  }

  // Default fallback - if autoparse is true, assume JSON
  if (autoparse === true) {
    return OUTPUT_FORMATS['JSON'].text;
  }

  // Otherwise, assume "Do not parse"
  return OUTPUT_FORMATS['Do not parse results'].text;
};

/**
 * Get format key from display text
 * @param text - The display text shown to the user
 * @returns The internal format key
 */
export const getFormatKeyFromText = (text: string): FormatKey => {
  const entry = Object.entries(OUTPUT_FORMATS).find(
    ([_, config]) => config.text === text
  );
  return (entry?.[0] as FormatKey) || 'JSON';
};

/**
 * Get array of display texts for dropdowns
 */
export const getFormatOptions = (): string[] => {
  return Object.values(OUTPUT_FORMATS).map(config => config.text);
};
